import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { TABLES } from "../../../Apis/Tables";
import ResidentAssignedCard from "../SubComponents/Residents/ResidentAssignedCard";
import UnitAssignedCard from "../SubComponents/Units/UnitAssignedCard";
import { getFullName } from "../../../Apis/functions";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { GetBuildingUserAssigned } from "../../../reduxToolkit/Building/BuildingApi";
import { getResidentVerificationStatus } from "../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Assigned logic of the building list detail expand: the read-only residents,
 * units and employees lists rendered by BuildingRenderDetails through
 * AssignedTabResource in readOnly mode. It only exposes the per-resource config,
 * the request body builder and the assigned endpoint; there is no deletion in
 * this view, reading the building is enough to open it.
 *
 * @param {Object} props
 * @param {string} props.resource - Resource table handled by the section.
 * @param {number|string|null|undefined} props.id - Building id.
 */

export const AssignedBuildingDetailsResourceApp = ({ resource, id }) => {
    const { t } = useTranslation();
    // The employees of the building have their own endpoint, the residents and
    // the units are read through the simple search by id
    const isEmployees = resource === TABLES.BUILDING_USER;

    /**
     * Renders a unit assigned card (unit name + resident count) for the grid.
     * @param {Object} args - Row render args from the table.
     * @returns {JSX.Element}
     */
    const renderUnitsItem = ({ item, keyId }) => {
        return (
            <UnitAssignedCard
                t={t}
                keyId={keyId}
                item={{
                    ...item,
                    [keyId]: item[keyId],
                    disabled: false,
                }}
            />
        );
    }

    /**
     * Renders a resident assigned card (name, verification status, email and apartment).
     * @param {Object} args - Row render args from the table.
     * @returns {JSX.Element}
     */
    const renderResidentsItem = ({ item, keyId }) => {
        return (
            <ResidentAssignedCard
                keyId={keyId}
                item={{
                    ...item,
                    [keyId]: item[keyId],
                    // The service returns the name split and the unit by its name
                    fullName: item?.fullName || getFullName(item?.name, item?.lastName, item?.secondLastName),
                    apartment: item?.apartment || item?.unitName,
                    // The chip of the card is the verification status of the resident
                    status: getResidentVerificationStatus(item?.identityValidationId),
                    disabled: false,
                }}
            />
        );
    }

    const dynamicConfig = useMemo(() => {
        if (resource === TABLES.BUILDING_USER) {
            return {
                keyId: 'userId',
                model: TABLES.BUILDING_USER,
                labelRowsPerPage: 'employees_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_employee_yet',
                searchLabel: 'search_by_name_email_or_phone',
            };
        }

        if (resource === TABLES.BUILDING_APARTMENT) {
            return {
                keyId: 'id',
                model: TABLES.BUILDING_APARTMENT,
                labelRowsPerPage: 'units_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_unit_yet',
                searchLabel: 'search_by_name',
                renderAssignedItem: renderUnitsItem,
            };
        }

        return {
            keyId: 'id',
            model: TABLES.BUILDING_APARTMENT_RESIDENT,
            labelRowsPerPage: 'residents_per_page',
            tableNotInformationLegend: 'you_have_not_assigned_any_resident_yet',
            searchLabel: 'search_by_name_email_or_apartment',
            renderAssignedItem: renderResidentsItem,
        }
    }, [resource]);

    /**
     * Assigned request body, the same search the lists of the detail run. Uses
     * the `searchElement` argument forwarded by getData (not the closure) so
     * reset/clear refetches with the right value.
     *
     * The building travels as `modelId` for the employees and as `userId` for the
     * residents and the units, each one is the id its endpoint filters by, and
     * GetBuildingUserAssigned takes the body itself while SimpleSearchWithId
     * expects it wrapped.
     */
    const buildBody = ({ page, rowsPerPage, searchElement }) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: dynamicConfig.model,
            key: searchElement,
            ...(isEmployees ? { modelId: id ?? null } : { userId: id })
        });

        return isEmployees ? body : { body };
    };

    return {
        t,
        dynamicConfig,
        assignedEndPoint: isEmployees ? GetBuildingUserAssigned : SimpleSearchWithId,
        buildBody,
    }
}
